import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Crown, Calendar, Sparkles, Award, ArrowRight, Building2, Clock, Utensils, Users } from 'lucide-react'
import { Link } from 'react-router-dom'

// Timeline Event Data
const timelineEvents = [
  {
    year: '1642',
    title: 'WINTERSCHLACHT',
    description: 'Im Winter 1642 fand in der Nähe des heutigen Standorts eine bedeutende militärische Auseinandersetzung statt. Die strategische Position an der Landwehr spielte eine entscheidende Rolle in den Kampfhandlungen.',
    detail: '47.000 Mann',
    era: 'Frühe Geschichte'
  },
  {
    year: '1758',
    title: 'SCHLACHT BEI KREFELD',
    description: 'Die Schlacht bei Krefeld im Jahr 1758 war Teil des Siebenjährigen Krieges. Die Landwehr und ihre Umgebung dienten als strategischer Punkt für die Aufstellung und Koordination der Truppen.',
    detail: '32.000 Mann',
    era: 'Militärische Bedeutung'
  },
  {
    year: '1855',
    title: 'FERTIGSTELLUNG DES GEBÄUDES',
    description: 'Das historische Gebäude an der Landwehr wurde 1855 fertiggestellt. Als Teil der preußischen Befestigungsanlagen diente es zunächst militärischen Zwecken.',
    detail: 'Est. Krefeld 1855',
    era: 'Gründung',
  },
  {
    year: '1900 - 1973',
    title: 'INDUSTRIELLER WANDEL & PACHTZEIT',
    description: 'Wandel von militärischer Nutzung zu industrieller und gewerblicher Verwendung. Die Pachtzeit durch Lönne/Ross prägte die Entwicklung des Standorts maßgeblich.',
    era: 'Transformation'
  },
  {
    year: '1998',
    title: 'BEGINN DER GASTRONOMIE',
    description: 'Nach umfassender Sanierung und Modernisierung durch Till Reese wurde 1998 die gastronomische Nutzung aufgenommen. Das historische Gebäude wurde behutsam restauriert.',
    detail: 'Gastronomie seit 1998',
    era: 'Neuzeit'
  },
  {
    year: '2024',
    title: 'ZWEITE GENERATION',
    description: 'Felix Reese führt das Erbe fort und entwickelt Hückels May weiter zu einem modernen Ort der Begegnung, der seine historischen Wurzeln ehrt.',
    era: 'Heute'
  }
]

// Stats
const stats = [
  { number: '1855', label: 'Erbaut', icon: Building2 },
  { number: '170+', label: 'Jahre Geschichte', icon: Clock },
  { number: '1998', label: 'Gastronomie seit', icon: Utensils },
  { number: '2', label: 'Generationen', icon: Users }
]

export default function Geschichte() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Compact Banner
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Geschichte von Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Ruby Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Crown className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Seit 1855</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Unsere <span className="italic text-white/70">Geschichte</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-xl mb-6 leading-relaxed"
          >
            Von militärischen Auseinandersetzungen bis zur modernen Gastronomie – ein lebendiges Zeugnis bewegter Historie.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <stat.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{stat.number}</p>
                <p className="text-white/40 text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TIMELINE SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        {/* Background Glows */}
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-20">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Zeitreise
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light"
            >
              Meilensteine unserer <span className="italic text-white/60">Historie</span>
            </motion.h2>
          </div>

          {/* Timeline Events */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-800/30 to-transparent" />

            <div className="space-y-16">
              {timelineEvents.map((event, index) => {
                const isEven = index % 2 === 0
                
                return (
                  <motion.div
                    key={event.year}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="relative grid md:grid-cols-2 gap-8 md:gap-16 items-center"
                  >
                    {/* Timeline Dot */}
                    <div className="hidden md:block absolute left-1/2 top-8 -translate-x-1/2 z-10">
                      <div className="relative">
                        <div className="absolute inset-0 w-4 h-4 bg-brand-800/30 rounded-full blur-md" />
                        <div className="relative w-4 h-4 bg-brand-800 rounded-full border-4 border-[#0a0a0a]" />
                      </div>
                    </div>

                    {/* Year Side */}
                    <div className={`${isEven ? 'md:text-right md:pr-12' : 'md:order-2 md:pl-12'}`}>
                      {/* Era Badge */}
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-4">
                        <Sparkles className="w-3 h-3 text-brand-400" />
                        <span className="text-brand-400 text-xs uppercase tracking-[0.2em]">{event.era}</span>
                      </div>

                      {/* Year */}
                      <div className="mb-4">
                        <span className="font-serif text-4xl md:text-5xl font-light text-brand-400">{event.year}</span>
                      </div>

                      {/* Detail Badge */}
                      {event.detail && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                          <Award className="w-3 h-3 text-brand-400" />
                          <span className="text-white/60 text-xs">{event.detail}</span>
                        </div>
                      )}
                    </div>

                    {/* Content Side */}
                    <div className={`${isEven ? 'md:pl-12' : 'md:order-1 md:pr-12'}`}>
                      <div className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-brand-800/30 transition-all duration-500">
                        <div className="h-px w-12 bg-brand-800 mb-4" />
                        <h3 className="font-serif text-xl md:text-2xl text-white font-light mb-4">{event.title}</h3>
                        <p className="text-white/50 leading-relaxed">{event.description}</p>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          ETYMOLOGIE SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="absolute bottom-40 left-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="p-8 md:p-12 bg-gradient-to-br from-white/[0.03] to-transparent border border-white/10 rounded-2xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/10 border border-brand-800/20 rounded-full mb-6">
              <Award className="w-3 h-3 text-brand-400" />
              <span className="text-brand-400 text-xs uppercase tracking-[0.2em]">Etymologie</span>
            </div>

            <h3 className="font-serif text-3xl md:text-4xl text-white font-light mb-6">
              Die Bedeutung von <span className="text-brand-400">"May"</span>
            </h3>

            <div className="h-px w-20 bg-brand-800 mb-6" />

            <p className="text-white/70 text-lg leading-relaxed mb-4">
              Der Name <span className="text-brand-400 font-semibold">"May"</span> leitet sich von <span className="text-white">"Maa"</span> oder <span className="text-white">"Mai"</span> ab, 
              was im regionalen Dialekt einen zollpflichtigen Übergang oder eine Schranke bezeichnete.
            </p>
            <p className="text-white/50 leading-relaxed">
              Dies verweist auf die historische Funktion des Ortes als Grenzpunkt und Kontrollstelle 
              entlang der Landwehr – ein authentisches Zeugnis der bewegten Vergangenheit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Crown className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Erleben Sie Geschichte</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Besuchen Sie uns im<br />
              <span className="italic text-white/60">historischen Ambiente</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Erleben Sie die Geschichte unseres Landguts bei einer persönlichen Besichtigung.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                Besichtigung anfragen
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
